<script>
  import YoutubeIframe from "../molecules/YoutubeIframe.svelte";
  import Icon from "../atoms/Icon.svelte";
  import Image from "../atoms/Image.svelte";
  import Button from "../atoms/Button.svelte";
  import { extractYoutubeVideoId } from "../utilities/youtubeIframeApi";
  import DynamicHeadingContext from "../atoms/DynamicHeadingContext.svelte";
  import DynamicHeading from "../atoms/DynamicHeading.svelte";

  /**
   * @typedef {Object} LoopSection
   * @property {number} startTime - The start time of the loop section in seconds.
   * @property {number} endTime - The end time of the loop section in seconds.
   */

  /**
   * @typedef {Object} Video
   * @property {import($lib/types/shared.js').Image} videoCoverImage - The cover image for the video.
   * @property {string} caption - The caption for the video.
   * @property {string} header - The header for the video.
   * @property {string} body - The body content for the video.
   * @property {string} className - Used to style the container of the Video component
   * @property {string} id - Primarily used for testing
   * @property {string} videoUrl - The URL of the YouTube video to embed.
   * @property {LoopSection} loopSection - Loop section of the video for autoplay behavior.
   */

  let className = "";
  export { className as class };
  export let id = undefined;
  export let videoUrl = undefined;
  export let videoId;
  export let videoCoverImage = undefined;
  export let caption = undefined;
  export let header = undefined;
  export let body = undefined;
  export let loopSection = undefined;
  export let isLiveStreamVideo = undefined;

  // TODO: this videoUrl logic needs to be cleaned up, because currently Contentful does not have this field on Video so we will never have videoUrl, but must have videoId
  if (!videoId && videoUrl) {
    videoId = extractYoutubeVideoId(videoUrl);
  }

  const hasLoopingSection = !!loopSection && loopSection.startTime >= 0 && loopSection.endTime > 0;
  let autoplay = hasLoopingSection;

  let hasVideoStartedFromBeginning = false;
  let youtube;

  const youtubeTogglePause = () => {
    youtube.togglePause();
  };

  const youtubeStartVideoFromBeginning = () => {
    youtube.startVideoFromBeginning();
    hasVideoStartedFromBeginning = true;
    autoplay = true;
    loopSection = { startTime: 0 };
  };

  const playPauseButtonClass = hasLoopingSection ? "right-5" : "left-5";

  // TODO: find a way to detect live stream YouTube video;  Currently implement this for democracy forum page
  $: isLiveStreamVideo =
    isLiveStreamVideo || videoId === "zklZVvrRZY0" || videoId === "E2xp8np3iM0";
  $: fixedWidthClass = isLiveStreamVideo ? "w-60" : "w-20";
  $: leftPositionClass = isLiveStreamVideo ? "left-[calc(50%-120px)]" : "left-[calc(50%-40px)]";

  let isPlayingLoopSection = autoplay;

  const handleLoopToggle = () => {
    youtubeTogglePause();
    isPlayingLoopSection = !isPlayingLoopSection;
  };

  /**
   * @type {string}
   * @description The height classes for the video container.
   *
   * This class sets the maximum height for a video on large. There is a magic number here:
   * 16px - This is the padding between the video and the content below it.
   */
  let videoHeightClasses = "lg:max-h-[calc(100vh-var(--nav-height)-16px)]";
</script>

<DynamicHeadingContext>
  <div {id} class={className}>
    {#if videoId}
      <div class="override-video relative {videoHeightClasses}">
        <YoutubeIframe
          bind:this={youtube}
          aspectRatioClasses="aspect-16/9"
          shouldShowControls={hasVideoStartedFromBeginning}
          shouldAutoPlay={autoplay}
          {videoId}
          {loopSection}
        />
        {#if !hasVideoStartedFromBeginning}
          <div
            class="absolute left-0 top-0 z-[--stack-level-video-iframe] h-full w-full bg-transparent"
            on:click={youtubeStartVideoFromBeginning}
            on:keydown={youtubeStartVideoFromBeginning}
            role="button"
            tabindex="0"
            aria-label="Watch Video"
          >
            {#if !isPlayingLoopSection && videoCoverImage && videoCoverImage.src}
              <Image
                {...videoCoverImage}
                aspectRatioClasses="aspect-16/9"
                elementClass={videoHeightClasses}
                containerClass="{videoHeightClasses} lg:mx-auto"
              />
            {/if}
          </div>

          {#if hasLoopingSection}
            <Button
              buttonClass="override-[video]:absolute bottom-5 left-5 z-[--stack-level-video-controls]"
              on:click={youtubeStartVideoFromBeginning}
              variant="filled"
              label="Watch Video"
              theme="black"
            >
              <Icon slot="icon" name="playOffset" />
            </Button>
          {/if}

          {#if !autoplay}
            <Button
              buttonClass="override-[video]:absolute bottom-[calc(50%-40px)] h-20 z-[--stack-level-video-controls] !bg-white hover:!bg-black justify-center whitespace-nowrap {playPauseButtonClass} {fixedWidthClass} {leftPositionClass} "
              on:click={hasLoopingSection ? handleLoopToggle : youtubeStartVideoFromBeginning}
              variant="filled"
              theme="black"
              ariaLabel={isPlayingLoopSection ? "Pause autoplay" : "Resume autoplay"}
              label={isLiveStreamVideo ? "Start livestream" : ""}
            >
              <Icon
                slot="icon"
                iconClass="text-inherit"
                name={isPlayingLoopSection ? "pause" : "play"}
              />
            </Button>
          {/if}
        {/if}

        <!--Overlay for the video-->
        {#if !hasVideoStartedFromBeginning}
          <div
            class="absolute left-0 top-0 z-[--stack-level-overlay] aspect-16/9 h-full bg-black-20 lg:right-0 lg:mx-auto {videoHeightClasses}"
          ></div>
        {/if}
      </div>
    {/if}
    {#if caption}
      <p class="body-sm pt-3">
        {caption}
      </p>
    {/if}
    {#if header?.trim().length > 0}
      <DynamicHeading class="heading-xs pt-8">
        {header}
      </DynamicHeading>
    {/if}
    {#if body}
      <p class="body-md pt-4">
        {body}
      </p>
    {/if}
  </div>
</DynamicHeadingContext>
